import {
  Component,
  OnInit,
  HostBinding,
  Input,
  ElementRef,
  Output,
  EventEmitter,
} from '@angular/core'
import { DomSanitizer } from '@angular/platform-browser'
import { AuthenticationService } from '../../services'

export interface IFilter {
  icon?: string
  placeholder?: string
  filterFunc?: (item: any, inputValue: string) => any
}

@Component({
  standalone: false,
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
})
export class TableComponent implements OnInit {
  @Input() items: any[]
  cachedItems: any[] = []
  @Input() clickable = false
  @Input() entityName: string
  @Input() noDataMessage: string
  @Input() selectable = true
  _itemsPerPage: number

  @Input() filter: IFilter
  @Input() loading = false

  get itemsSelected() {
    return this.items.filter((item) => item.selected)
  }

  @Input()
  get itemsPerPage() {
    return this._itemsPerPage
  }

  set itemsPerPage(items) {
    this._itemsPerPage = items
  }

  pageItems: any[]

  cols: any[]

  @Input()
  get columns() {
    return this.cols
  }

  set columns(cols) {
    this.cols = cols
  }

  @HostBinding('style')
  get style() {
    return this.sanitizer.bypassSecurityTrustStyle(
      `--table-columns: ${this.columns.length};`,
    )
  }

  @Output() tableItemClickEvent = new EventEmitter<any>()
  @Output() onCheckboxClick = new EventEmitter<any>()

  private sortOrder = true // Descendent order
  private noData = false

  constructor(
    private tableElement: ElementRef,
    private sanitizer: DomSanitizer,
    private auth: AuthenticationService,
  ) {}

  ngOnInit() {
    this.columns = this.columns || Object.keys(this.items[0])
    this.cachedItems = this.items
    this.pageItems = this.items
  }

  /**
   * Filter table
   *
   * @param $event
   */
  filterTable($event) {
    const inputValue = $event.target.value

    const defaultFilter = (item) =>
      Object.values(item)
        .map((value) => String(value))
        .find((value) => value.toLowerCase().includes(inputValue.toLowerCase()))
    const data = this.items.filter((item) => {
      return (this.filter.filterFunc || defaultFilter)(item, inputValue)
    })

    this.noData = !data.length

    this.cachedItems = data
    this.pageItems = data
  }

  /**
   * Sort table
   *
   * @param column : string - property
   * @param order: boolean -
   */
  sortTable($event, column: string) {
    $event.preventDefault()
    $event.stopPropagation()

    this.setSelectedHeader($event.target)
    this.cachedItems.sort(this.sortByProp(column))
  }

  setSelectedHeader(target) {
    const headerCells = this.tableElement.nativeElement.querySelectorAll(
      '.table-header .table-cell',
    )
    ;[].forEach.call(headerCells, (cell) => {
      if (cell === target) {
        if (cell.classList.contains('selected')) {
          this.sortOrder = !this.sortOrder
          cell.classList.toggle('order')
        } else {
          this.sortOrder = true
          cell.classList.add('selected')
        }
      } else {
        cell.classList.remove('selected')
        cell.classList.remove('order')
      }
    })
  }

  sortByProp(prop: string) {
    return (a, b) => {
      if (this.sortOrder === false) {
        return b[prop].localeCompare(a[prop])
      } else {
        return a[prop].localeCompare(b[prop])
      }
    }
  }

  /**
   * Table row click handler
   *
   * @param item :Object - the table item clicked
   */
  tableRowClick(item) {
    this.tableItemClickEvent.emit(item)
  }

  checkboxClick(item) {
    this.onCheckboxClick.emit(item)
  }

  isAllSelected() {
    return (
      this.pageItems.length !== 0 &&
      this.pageItems.filter((item) => item.selected).length ===
        this.pageItems.length
    )
  }

  toggleSelectAll() {
    if (
      !this.isAllSelected() &&
      this.pageItems.filter((item) => item.selected).length !== 0
    ) {
      this.pageItems.forEach((item) => {
        item.selected = false
        this.checkboxClick(item)
      })
    } else {
      this.pageItems.forEach((item) => this.checkboxClick(item))
    }
  }

  getIsAdmin(item, columnId) {
    return columnId === 'username' && item.isAdmin
  }

  isMe(item, columnId) {
    return (
      columnId === 'username' &&
      this.auth.getUser().user.username === item.username
    )
  }
}
